import { Star } from "lucide-react";
import { Icon } from "@cntrl/ui";
import { useMe, useSetStarredBand } from "../../app/hooks/useSession.js";
import styles from "./StarBandButton.module.scss";

/**
 * The star beside a band in the band switcher (desktop) and the account sheet (mobile) — ART-319. The starred
 * band is the one the app opens on when this device remembers no band (a fresh open; see `activeBand.ts`).
 * One band at a time: starring another moves the star, pressing a lit star clears it. Saved on the account, so
 * every device shares it.
 *
 * The lit state is a SOLID brand-crimson star (PO, ART-319) — the second sanctioned exception to `Icon`'s
 * outline-only rule (brand guide §12), after media transport — and `aria-pressed` carries it for a screen
 * reader. Callers render it only when the person has more than one band; with one there is nothing to choose.
 */
export function StarBandButton({ bandId, bandName }: { bandId: string; bandName: string }) {
  const me = useMe();
  const setStarred = useSetStarredBand();
  const starred = me.data?.starredBandId === bandId;
  return (
    <button
      type="button"
      className={styles.star}
      data-starred={starred || undefined}
      aria-pressed={starred}
      aria-label={starred ? `${bandName} is your default band` : `Make ${bandName} your default band`}
      title={starred ? "Default band — the app opens here" : "Open the app on this band"}
      disabled={setStarred.isPending}
      onClick={(e) => {
        e.stopPropagation();
        setStarred.mutate(starred ? null : bandId);
      }}
    >
      <Icon icon={Star} size={16} variant={starred ? "filled" : "outline"} />
    </button>
  );
}
